import { FilterIdentifier, getFilterGroupById } from "@/data/filters/config";
import { getConfigurationCounts } from "@/queries/configurations";
import { getFilterGroupsFromConfigurations } from "@/services/configurations.service";
import { formatContactsSidebarLabel } from "@/services/contacts.service";
import type {
  Configuration,
  DepartmentSizeQueryFilter,
  EventQueryFilter,
  QueryFilter,
  StopList,
} from "@/types/campaign";
import {
  detectCurrentStrategy,
  getContactConfiguration,
  isSegmentDisabled,
} from "@/utils/configurations";
import { formatNumber } from "@/utils/number-utils";
import { useQuery } from "@tanstack/react-query";
import { Link, linkOptions, ToOptions } from "@tanstack/react-router";
import { groupBy } from "lodash-es";
import { Ellipsis, TrashIcon } from "lucide-react";
import { Dispatch, SetStateAction, useMemo, useState } from "react";
import { Button, MenuTrigger } from "react-aria-components";
import { twMerge } from "tailwind-merge";
import { AlertModal } from "../generic/AlertModal";
import { Menu, MenuItem } from "../generic/Menu";
import { composeTailwindRenderProps, focusRing } from "../generic/utils";

const expectedQualities = ["1", "2", "3"] as const; // Gold, Silver, Bronze

const qualityColors: Partial<
  Record<string, { text: string; background: string }>
> = {
  "1": {
    text: "text-theme-on-background",
    background: "bg-theme-gold",
  },
  "2": {
    text: "text-theme-on-background",
    background: "bg-theme-silver",
  },
  "3": {
    text: "text-theme-on-background",
    background: "bg-theme-bronze",
  },
};

const getSegmentColor = (quality: string) => {
  return qualityColors[quality]?.text;
};

const getSegmentBackgroundColor = (quality: string) => {
  return qualityColors[quality]?.background;
};

export type SegmentsTableLinkContext = {
  /** Link for editing a specific filter group on a quality segment */
  editFilter: (
    category: string,
    filterId: string,
    quality: number,
  ) => ToOptions;
  /** Link for editing contact personas on a quality segment */
  contacts: (quality: number) => ToOptions;
};

type SegmentsTableProps = {
  configurations: Configuration[];
  setConfigurations: Dispatch<SetStateAction<Configuration[]>>;
  className?: string;
  baseFilters?: (QueryFilter | EventQueryFilter | DepartmentSizeQueryFilter)[];
  linkContext: SegmentsTableLinkContext;
  /**
   * Optional pre-fetched counts keyed by segment quality.
   * When provided the internal /counts query is skipped and these values are
   * used directly. Useful when counts are already available from a /suggest
   * response (which returns counts for ALL qualities, not just enabled ones).
   */
  externalCounts?: Map<number, number>;
  stopLists: StopList[];
  /** Set for an existing campaign, so counts exclude leads it has already delivered. */
  campaignId?: number;
};

export function SegmentsTable({
  className,
  configurations,
  setConfigurations,
  linkContext,
  baseFilters,
  externalCounts,
  stopLists,
  campaignId,
}: SegmentsTableProps) {
  const groupedConfigurations = groupBy(configurations, "quality");
  const [isDeleteContact, setIsDeleteContact] = useState(false);

  const filterGroups = getFilterGroupsFromConfigurations(configurations ?? []);

  const { data: countsData } = useQuery({
    ...getConfigurationCounts({
      configurations,
      stopListIds: stopLists.map((s) => s.uniqueId.toString()),
      campaignId,
    }),
    // Skip the network request when the caller supplies counts externally.
    enabled: externalCounts === undefined,
  });

  const countsByQuality =
    externalCounts ??
    new Map(countsData?.map(({ quality, count }) => [quality, count]));

  const enabledCounts = formatNumber(
    Array.from(countsByQuality.entries())
      .filter(([quality]) => {
        const configs = groupedConfigurations[quality.toString()];
        return configs && !isSegmentDisabled(configs);
      })
      .reduce((sum, [, count]) => sum + count, 0),
  );

  const toggleGroupDisabled = (quality: number) => {
    setConfigurations((prev) => {
      const isGroupEnabled = prev.some(
        (c) => c.quality === quality && c.enabled,
      );

      const toggled = prev.map((c) =>
        c.quality === quality ? { ...c, enabled: !isGroupEnabled } : c,
      );

      if (detectCurrentStrategy(toggled).label !== "round_robin") {
        return toggled;
      }

      const enabled = toggled.filter((c) => c.enabled);
      const n = enabled.length || 1;
      const evenShare = Math.floor(100 / n);

      return toggled.map((c) => ({
        ...c,
        idealLeadShare: c.enabled ? evenShare : 0,
      }));
    });
  };

  const onDeleteContactconfiguration = () => {
    setIsDeleteContact(true);

    setConfigurations((prev) => {
      return prev.map((c) => {
        if (c.contactConfiguration === null) return c;
        return {
          ...c,
          contactConfiguration: null,
        };
      });
    });
  };

  const contactConfiguration = useMemo(
    () => getContactConfiguration(configurations),
    [configurations],
  );

  if (!configurations?.length && baseFilters) {
    const filterGroups = getFilterGroupsFromConfigurations([
      {
        contactConfiguration: null,
        idealLeadShare: 100,
        estimatedRemaining: null,
        priority: 1,
        enabled: true,
        name: "Guld",
        quality: 1,
        query: {
          filters: baseFilters,
          orderBy: "id",
          descendingOrder: false,
          limit: 100,
          offset: 0,
        },
      },
    ]);

    const groupedConfigurations = groupBy(
      [
        {
          contactConfiguration: null,
          idealLeadShare: 100,
          estimatedRemaining: null,
          priority: 1,
          enabled: true,
          name: "Guld",
          quality: 1,
          query: {
            filters: baseFilters,
            orderBy: "id",
            descendingOrder: false,
            limit: 100,
            offset: 0,
          },
        },
      ],
      "quality",
    );

    return (
      <div className={twMerge("flex-1", className)}>
        {isDeleteContact && (
          <AlertModal
            title="Fjern filter?"
            description={`Hvis du fjerner "Kontaktpersoner" bliver det fjernet fra Guld, Sølv og Bronze, og dine estimater vil ændre sig`}
            icon={<TrashIcon className="text-theme-active" />}
            isOpen={isDeleteContact}
            onClose={() => setIsDeleteContact(false)}
            onConfirm={() => {
              onDeleteContactconfiguration();
            }}
          />
        )}
        <table className="mb-0 w-full">
          <tbody className="flex flex-col">
            <tr className="grid grid-cols-4 gap-x-4">
              <TableHeader className="min-h-5">
                <span className="text-[13px] font-medium">Målgruppe</span>
              </TableHeader>

              <TableHeader
                className="flex min-h-5 items-center justify-between"
                separatorClassName={getSegmentBackgroundColor("1")}
              >
                <span
                  className={twMerge(
                    "text-[13px] font-medium",
                    getSegmentColor("1"),
                  )}
                >
                  Guld
                </span>
              </TableHeader>

              {[...Array(2)].map((_, i) => (
                <TableHeader key={i} className="min-h-5">
                  <div className="bg-theme-on-background-variant/25 h-5 grow animate-pulse rounded-md" />
                </TableHeader>
              ))}
            </tr>

            {Array.from(filterGroups.keys()).map((filterGroupId) => (
              <TableRow
                key={filterGroupId}
                configurations={configurations}
                setConfigurations={setConfigurations}
                filterGroupId={filterGroupId}
                groupedConfigurations={groupedConfigurations}
                linkContext={linkContext}
              />
            ))}

            {contactConfiguration && (
              <tr className="group grid grid-cols-4 gap-x-4">
                <TableCell as="th" scope="row">
                  <div className="flex w-full flex-nowrap items-center justify-between">
                    <span className="truncate px-3 py-2 text-start text-[11px] font-bold whitespace-nowrap">
                      Kontaktpersoner
                    </span>
                    <MenuTrigger>
                      <Button
                        className={composeTailwindRenderProps(
                          focusRing,
                          "hover:text-theme-secondary -m-2 flex h-auto cursor-pointer rounded-full p-2 opacity-0 transition-opacity group-hover:opacity-100",
                        )}
                      >
                        <Ellipsis className="size-5" />
                      </Button>
                      <Menu
                        placement="bottom end"
                        onAction={() => setIsDeleteContact(true)}
                      >
                        <MenuItem>Fjern filter</MenuItem>
                      </Menu>
                    </MenuTrigger>
                  </div>
                </TableCell>

                {expectedQualities.map((quality) => {
                  const configs = groupedConfigurations[quality];
                  if (configs) {
                    return (
                      <ContactsTableCell
                        key={quality}
                        quality={quality}
                        configurations={configs}
                        allConfigurations={groupedConfigurations}
                        isDisabled={isSegmentDisabled(configs)}
                        linkContext={linkContext}
                      />
                    );
                  }

                  return (
                    <TableCell key={quality}>
                      <div className="bg-theme-on-background-variant/25 mx-3 h-8 grow animate-pulse rounded-md" />
                    </TableCell>
                  );
                })}
              </tr>
            )}

            <tr className="grid grid-cols-4 gap-x-4">
              {expectedQualities.map((quality) => {
                const configs = groupedConfigurations[quality];
                if (configs) {
                  return (
                    <TableCell
                      isLast
                      isDisabled={isSegmentDisabled(configs)}
                      key={quality}
                    />
                  );
                }

                return <TableCell key={quality} isLast />;
              })}
            </tr>
          </tbody>
        </table>
      </div>
    );
  }

  return (
    <div>
      {isDeleteContact && (
        <AlertModal
          title="Fjern filter?"
          description={`Hvis du fjerner "Kontaktpersoner" bliver det fjernet fra Guld, Sølv og Bronze, og dine estimater vil ændre sig`}
          icon={<TrashIcon className="text-theme-active" />}
          isOpen={isDeleteContact}
          onClose={() => setIsDeleteContact(false)}
          onConfirm={() => {
            onDeleteContactconfiguration();
          }}
        />
      )}
      <table className="mb-0 w-full">
        <tbody className="flex flex-col">
          <tr className="grid grid-cols-4 gap-x-4">
            <TableHeader className="min-h-5">
              <div className="flex flex-col items-start gap-1">
                <span className="text-[13px] font-medium">Målgruppe</span>
                <span className="text-[11px] font-medium xl:mt-2">
                  {enabledCounts} leads i alt
                </span>
              </div>
            </TableHeader>

            {Object.entries(groupedConfigurations).map(
              ([quality, _configurations]) => {
                const countData = formatNumber(
                  countsByQuality.get(Number(quality)) ?? 0,
                );
                return (
                  <TableHeader
                    key={quality}
                    className="flex min-h-5 flex-col"
                    separatorClassName={getSegmentBackgroundColor(quality)}
                  >
                    <div className="flex flex-col gap-1">
                      <span
                        className={twMerge(
                          "text-left text-[13px] font-medium",
                          getSegmentColor(quality),
                        )}
                      >
                        {_configurations[0].name}
                      </span>

                      {countData ? (
                        <span className="text-theme-on-background w-full text-left text-[11px] font-medium xl:mt-2">
                          {!isSegmentDisabled(_configurations) &&
                          _configurations[0].quality === 1 ? (
                            <>{countData}</>
                          ) : (countsByQuality.get(
                              Number(_configurations[0].quality),
                            ) ?? 0) > 0 ||
                            !isSegmentDisabled(_configurations) ? (
                            <Button
                              className="text-theme-active w-full cursor-pointer text-left"
                              onPress={() =>
                                toggleGroupDisabled(Number(quality))
                              }
                            >
                              {isSegmentDisabled(_configurations)
                                ? "Tilføj"
                                : "Fjern"}{" "}
                              {countData ? countData : "segment"}
                            </Button>
                          ) : (
                            <span className="text-theme-on-background-variant">
                              Ikke tilgængelig
                            </span>
                          )}
                        </span>
                      ) : (
                        <div className="bg-theme-on-background-variant/25 mt-2 h-4 w-full grow animate-pulse rounded-md" />
                      )}
                    </div>

                    {/*<SegmentsMenu
                      className={quality !== "1" ? "" : "invisible"}
                      quality={Number(quality)}
                      isDisabled={isSegmentDisabled(_configurations)}
                      onToggleDisabled={() =>
                        toggleGroupDisabled(Number(quality))
                      }
                    />*/}
                  </TableHeader>
                );
              },
            )}
          </tr>

          {Array.from(filterGroups.keys()).map((filterGroupId) => (
            <TableRow
              key={filterGroupId}
              configurations={configurations}
              setConfigurations={setConfigurations}
              filterGroupId={filterGroupId}
              groupedConfigurations={groupedConfigurations}
              linkContext={linkContext}
            />
          ))}

          {contactConfiguration && (
            <tr className="group grid grid-cols-4 gap-x-4">
              <TableCell as="th" scope="row">
                <div className="flex w-full flex-nowrap items-center justify-between">
                  <span className="truncate px-3 py-2 text-start text-[11px] font-bold whitespace-nowrap">
                    Kontaktpersoner
                  </span>
                  <MenuTrigger>
                    <Button
                      className={composeTailwindRenderProps(
                        focusRing,
                        "hover:text-theme-secondary -m-2 flex h-auto cursor-pointer rounded-full p-2 opacity-0 transition-opacity group-hover:opacity-100",
                      )}
                    >
                      <Ellipsis className="size-5" />
                    </Button>
                    <Menu
                      placement="bottom end"
                      onAction={() => setIsDeleteContact(true)}
                    >
                      <MenuItem>Fjern filter</MenuItem>
                    </Menu>
                  </MenuTrigger>
                </div>
              </TableCell>

              {Object.entries(groupedConfigurations).map(
                ([quality, configurations]) => (
                  <ContactsTableCell
                    key={quality}
                    quality={quality}
                    configurations={configurations}
                    allConfigurations={groupedConfigurations}
                    isDisabled={isSegmentDisabled(configurations)}
                    linkContext={linkContext}
                  />
                ),
              )}
            </tr>
          )}

          <tr className="grid grid-cols-4 gap-x-4">
            <TableCell as="th" scope="row" isLast></TableCell>

            {Object.entries(groupedConfigurations).map(([quality]) => (
              <TableCell isLast key={quality} />
            ))}
          </tr>
        </tbody>
      </table>
    </div>
  );
}

type TableRowProps = {
  configurations: Configuration[];
  setConfigurations: Dispatch<SetStateAction<Configuration[]>>;
  filterGroupId: string;
  groupedConfigurations: Record<string, Configuration[]>;
  linkContext: SegmentsTableLinkContext;
};

function TableRow({
  setConfigurations,
  filterGroupId,
  groupedConfigurations,
  linkContext,
}: TableRowProps) {
  const filterGroup = getFilterGroupById(filterGroupId);
  const [isDeleteFilter, setIsDeleteFilter] = useState(false);
  if (!filterGroup) return null;
  const { group, category } = filterGroup;

  const onDeleteFilter = () => {
    setConfigurations((prev) => {
      const filterGroup = getFilterGroupById(filterGroupId)?.group;
      if (!filterGroup) return prev;

      const filterIds: readonly string[] = filterGroup.filterType.filters;
      const groupFilters: Partial<Record<string, FilterIdentifier | null>> =
        filterGroup.filters;
      const filterNames = new Set(
        filterIds.map((id) => groupFilters[id]?.filterName),
      );

      return prev.map((c) => {
        const newQuery = c.query.filters.filter(
          (f) => !filterNames.has(f.filterName),
        );
        return {
          ...c,
          query: { ...c.query, filters: newQuery },
        };
      });
    });
  };

  const areFiltersDifferentFromGold = (quality: string): boolean => {
    if (quality === "1") return false;

    const goldConfigs = groupedConfigurations["1"];
    const currentConfigs = groupedConfigurations[quality];

    if (!goldConfigs || !currentConfigs) return false;

    const filterGroup = getFilterGroupById(filterGroupId);
    if (!filterGroup) return false;

    const filterIds = filterGroup.group.filterType.filters;

    const groupFilters: Record<string, FilterIdentifier | null> =
      filterGroup.group.filters;

    const filterNames = new Set(
      filterIds.map((id) => groupFilters[id]?.filterName),
    );

    const normalizeFilter = (
      item: QueryFilter | EventQueryFilter | DepartmentSizeQueryFilter,
    ) => {
      const filtered = Object.fromEntries(
        Object.entries(item).filter(([_, value]) => value != null),
      );

      return Object.fromEntries(
        Object.entries(filtered).sort(([a], [b]) => a.localeCompare(b)),
      );
    };

    const goldFilters = goldConfigs[0].query.filters
      .filter((f) => filterNames.has(f.filterName))
      .map(normalizeFilter);

    const currentFilters = currentConfigs[0].query.filters
      .filter((f) => filterNames.has(f.filterName))
      .map(normalizeFilter);

    return JSON.stringify(goldFilters) !== JSON.stringify(currentFilters);
  };

  if (isDeleteFilter) {
    return (
      <AlertModal
        title="Fjern filter?"
        description={`Hvis du fjerner "${group.title}" bliver det fjernet fra Guld, Sølv og Bronze, og dine estimater vil ændre sig.`}
        onConfirm={onDeleteFilter}
        onClose={() => setIsDeleteFilter(false)}
        icon={<TrashIcon className="text-theme-active" />}
        isOpen={isDeleteFilter}
      />
    );
  }

  return (
    <tr className="group grid grid-cols-4 gap-x-4">
      <TableCell as="th" scope="row" className="xl:pb-4">
        <div className="flex w-full flex-nowrap items-center justify-between">
          <span className="px-3 py-2 text-left text-[11px] font-bold wrap-break-word hyphens-auto">
            {group.title}
          </span>
          <MenuTrigger>
            <Button
              className={composeTailwindRenderProps(
                focusRing,
                "hover:text-theme-secondary flex h-auto cursor-pointer rounded-full p-2 opacity-100 transition-opacity xl:-m-2 xl:opacity-0 xl:group-hover:opacity-100",
              )}
            >
              <Ellipsis className="size-5" />
            </Button>
            <Menu
              placement="bottom end"
              onAction={() => setIsDeleteFilter(true)}
            >
              <MenuItem>Fjern filter</MenuItem>
            </Menu>
          </MenuTrigger>
        </div>
      </TableCell>

      {expectedQualities.map((quality) => {
        const configs = groupedConfigurations[quality];
        const isDifferentFromGold = areFiltersDifferentFromGold(quality);

        if (configs) {
          return (
            <TableCell
              key={quality}
              isDisabled={isSegmentDisabled(configs)}
              className="xl:pb-4"
            >
              <Link
                key={`${group.id}-${quality}`}
                {...linkContext.editFilter(
                  category.id,
                  group.id,
                  Number(quality),
                )}
                disabled={isSegmentDisabled(configs)}
                className={twMerge(
                  "not-disabled-or-aria-disabled:hover:bg-theme-on-background-hover disabled-or-aria-disabled:cursor-not-allowed disabled-or-aria-disabled:text-theme-on-background/20 size-full h-fit rounded-md px-3 py-2 text-[11px] font-medium",
                  isDifferentFromGold &&
                    !isSegmentDisabled(configs) &&
                    "font-bold",
                )}
              >
                <div className="empty:before:content-['-']">
                  <group.DisplayValue filters={configs[0].query.filters} />
                </div>
              </Link>
            </TableCell>
          );
        }
        return (
          <TableCell key={quality}>
            <div className="bg-theme-on-background-variant/25 mx-3 h-6 grow animate-pulse rounded-md" />
          </TableCell>
        );
      })}
    </tr>
  );
}

type TableHeaderProps = {
  children: React.ReactNode;
  separatorClassName?: string;
  isDisabled?: boolean;
} & React.ComponentProps<"th">;

function TableHeader({
  children,
  className,
  separatorClassName,
  isDisabled,
  ...props
}: TableHeaderProps) {
  return (
    <th
      className={twMerge(
        "border-theme-outline-variant flex flex-col rounded-t-sm border-x border-t px-3 pt-3 xl:px-4 xl:pt-7",
        isDisabled && "opacity-50",
      )}
      {...props}
    >
      <div className={twMerge("flex xl:px-3", className)}>{children}</div>
      <div
        className={twMerge(
          "bg-theme-outline-variant mt-4 mb-4 h-px xl:mx-3 xl:mb-8",
          separatorClassName,
        )}
      />
    </th>
  );
}

type TableCellProps = {
  children?: React.ReactNode;
  as?: React.ElementType;
  isLast?: boolean;
  isDisabled?: boolean;
} & React.ComponentProps<"td">;

function TableCell({
  children,
  as: Component = "td",
  className,
  isLast,
  isDisabled,
}: TableCellProps) {
  return (
    <Component
      className={twMerge(
        "border-theme-outline-variant flex border-x xl:px-4",
        isLast && "rounded-b-sm border-b pb-4 xl:pb-7",
        isDisabled && "pointer-events-none",
        className,
      )}
    >
      {children}
    </Component>
  );
}
type SegmentSettingsMenuProps = {
  className?: string;
  quality: number;
  isDisabled: boolean;
  onToggleDisabled: () => void;
};

function SegmentsMenu({
  className,
  isDisabled,
  onToggleDisabled,
}: SegmentSettingsMenuProps) {
  return (
    <MenuTrigger>
      <Button
        className={composeTailwindRenderProps(
          focusRing,
          twMerge(
            "hover:text-theme-secondary -m-2 flex h-auto cursor-pointer rounded-full p-2",
            className,
          ),
        )}
      >
        <Ellipsis className="size-5" />
      </Button>
      <Menu placement="bottom end">
        <MenuItem onAction={onToggleDisabled}>
          {isDisabled ? "Slå gruppe til" : "Slå gruppe fra"}
        </MenuItem>
      </Menu>
    </MenuTrigger>
  );
}

type ContactsTableCellProps = {
  quality: string;
  configurations: Configuration[];
  allConfigurations: Record<string, Configuration[]>;
  isDisabled?: boolean;
  linkContext: SegmentsTableLinkContext;
};

function ContactsTableCell({
  configurations,
  quality,
  allConfigurations,
  isDisabled = false,
  linkContext,
}: ContactsTableCellProps) {
  const isContactConfigDifferentFromGold = (): boolean => {
    if (quality === "1") return false;

    const goldConfigs = allConfigurations["1"];
    if (!goldConfigs) return false;

    const goldConfigWithContacts = goldConfigs.find(
      (c) =>
        c?.contactConfiguration?.personas &&
        c.contactConfiguration.personas.length > 0 &&
        c.quality === 1,
    );
    const goldConfigWithoutContacts = goldConfigs.find(
      (c) => c.contactConfiguration === null && c.quality === 1,
    );

    const currentConfigWithContacts = configurations.find(
      (c) =>
        c?.contactConfiguration?.personas &&
        c.contactConfiguration.personas.length > 0 &&
        c.quality === Number(quality),
    );
    const currentConfigWithoutContacts = configurations.find(
      (c) => c.contactConfiguration === null && c.quality === Number(quality),
    );

    const goldContactsEnabled = goldConfigWithContacts
      ? !isSegmentDisabled([goldConfigWithContacts])
      : false;
    const goldWithoutContactsEnabled = goldConfigWithoutContacts
      ? !isSegmentDisabled([goldConfigWithoutContacts])
      : false;
    const currentContactsEnabled = currentConfigWithContacts
      ? !isSegmentDisabled([currentConfigWithContacts])
      : false;
    const currentWithoutContactsEnabled = currentConfigWithoutContacts
      ? !isSegmentDisabled([currentConfigWithoutContacts])
      : false;

    if (
      goldContactsEnabled !== currentContactsEnabled ||
      goldWithoutContactsEnabled !== currentWithoutContactsEnabled
    ) {
      return true;
    }

    const goldContactConfig = goldConfigWithContacts?.contactConfiguration;
    const currentContactConfig =
      currentConfigWithContacts?.contactConfiguration;

    const normalizeConfig = (config: typeof goldContactConfig) => {
      if (!config) return null;
      return {
        requireContact: config.requireContact,

        personas:
          config?.personas?.map(({ titles, levels, groups }) => ({
            titles,
            levels,
            groups,
          })) ?? [],
      };
    };

    return (
      JSON.stringify(normalizeConfig(goldContactConfig)) !==
      JSON.stringify(normalizeConfig(currentContactConfig))
    );
  };

  const isDifferentFromGold = isContactConfigDifferentFromGold();

  return (
    <TableCell isDisabled={isDisabled}>
      <Link
        {...linkContext.contacts(Number(quality))}
        className={twMerge(
          "not-disabled-or-aria-disabled:hover:bg-theme-on-background-hover disabled-or-aria-disabled:cursor-not-allowed disabled-or-aria-disabled:text-theme-on-background/20 hover:bg-theme-on-background-hover size-full h-fit rounded-md px-3 py-2 text-[11px] font-medium",
          isDifferentFromGold && !isDisabled && "font-bold",
        )}
        disabled={isDisabled}
      >
        {formatContactsCellLabel(configurations, quality)}
      </Link>
    </TableCell>
  );
}

function formatContactsCellLabel(
  configurations: Configuration[],
  quality: string,
): string {
  const configWithContacts = configurations.find(
    (c) =>
      c?.contactConfiguration?.personas &&
      c.contactConfiguration.personas.length > 0 &&
      c.quality === Number(quality),
  );
  const configWithoutContacts = configurations.find(
    (c) => c.contactConfiguration === null && c.quality === Number(quality),
  );

  // If no contact configuration exists or all configurations are disabled
  if (!configWithContacts || isSegmentDisabled([configWithContacts])) {
    return "Ingen kontaktpersoner";
  }

  // If only contact configuration is enabled (contacts required)
  if (
    !isSegmentDisabled([configWithContacts]) &&
    (!configWithoutContacts || isSegmentDisabled([configWithoutContacts]))
  ) {
    return formatContactsSidebarLabel(configWithContacts.contactConfiguration!);
  }

  // If both configurations are enabled (contacts optional)
  if (
    configWithoutContacts &&
    !isSegmentDisabled([configWithContacts, configWithoutContacts])
  ) {
    const formattedLabel = formatContactsSidebarLabel(
      configWithContacts.contactConfiguration!,
    );
    return `Leads med eller uden ${formattedLabel.toLowerCase()}`;
  }

  return "Ingen kontaktpersoner";
}
